import React, { useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { DetailHeader } from '@/components/ui/detail-header';
import { PressableScale } from '@/components/ui/pressable-scale';
import { EmptyState } from '@/components/ui/empty-state';
import { AvailabilityPicker } from '@/components/schedule/availability-picker';
import { useAvailability, useRescheduleAppointment } from '@/hooks/use-appointments';
import { AvailabilitySlot } from '@/types/appointment';
import { useToastStore } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';
import { formatSlotTime } from '@/utils/format-appointment';
import { useThemedHex } from '@/hooks/use-theme-colors';

const BRAND = '#FD5001';
const HORIZON_DAYS = 30;

export default function RescheduleScreen() {
  const th = useThemedHex();
  const { id, providerId, serviceId, serviceName } = useLocalSearchParams<{
    id: string;
    providerId: string;
    serviceId: string;
    serviceName: string;
  }>();
  const { addToast } = useToastStore();
  const reschedule = useRescheduleAppointment();
  const [selected, setSelected] = useState<AvailabilitySlot | null>(null);

  const range = useMemo(() => {
    const from = new Date();
    const to = new Date(from.getTime() + HORIZON_DAYS * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const availability = useAvailability({ providerId, serviceId, ...range });
  const days = availability.data?.days ?? [];
  const hasSlots = days.some((d) => d.available && d.slots.length > 0);

  const handleConfirm = () => {
    if (!selected) return;
    reschedule.mutate(
      { id, data: { startAt: selected.startAt } },
      {
        onSuccess: () => {
          addToast('Agendamento remarcado!', 'success');
          router.back();
        },
        onError: (err: any) => {
          addToast(
            getApiErrorMessage(err, 'Horário indisponível. Seu agendamento original foi mantido.'),
            'error',
          );
          void availability.refetch();
          setSelected(null);
        },
      },
    );
  };

  return (
    <View className="flex-1 bg-neutral-50">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ paddingBottom: 120 }}
      >
        <View className="gap-6 p-5">
          <DetailHeader
            className="px-0"
            title="Remarcar"
            subtitle={serviceName}
            onBack={() => router.back()}
          />

          {availability.isLoading ? (
            <View className="items-center py-16">
              <ActivityIndicator size="large" color={BRAND} />
            </View>
          ) : hasSlots ? (
            <AvailabilityPicker
              days={days}
              brandColor={BRAND}
              selectedStart={selected?.startAt ?? null}
              onSelect={setSelected}
            />
          ) : (
            <EmptyState
              icon="calendar-clear-outline"
              title="Sem horários no período"
              description={`Não há vagas nos próximos ${HORIZON_DAYS} dias.`}
            />
          )}
        </View>
      </ScrollView>

      {hasSlots && (
        <View className="absolute bottom-0 left-0 right-0 border-t border-neutral-200 bg-card p-4">
          <PressableScale
            haptic
            disabled={!selected || reschedule.isPending}
            onPress={handleConfirm}
            className="w-full flex-row items-center justify-center gap-2 rounded-full py-4"
            style={{
              backgroundColor: selected ? BRAND : th('#D4D4D4'),
              borderCurve: 'continuous',
            }}
          >
            {reschedule.isPending ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <>
                <Ionicons name="swap-horizontal" size={20} color="#fff" />
                <ThemedText className="font-bold text-base text-white">
                  {selected
                    ? `Remarcar para ${formatSlotTime(selected.startAt)}`
                    : 'Selecione um horário'}
                </ThemedText>
              </>
            )}
          </PressableScale>
        </View>
      )}
    </View>
  );
}
